import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../auth/AuthProvider'
import { SeguruSymbol } from '../components/SeguruSymbol'

/**
 * LandingPage — sistema D2 ("branco silêncio").
 *
 * Reescrita 2026-04-26 a partir do handoff `landing.html`. Substitui a
 * versão cream/green anterior. Tokens em `styles/d2/tokens.css`,
 * regras de aplicação em `docs/BRAND-D2.md`, voz em `docs/BRAND-VOICE.md`.
 *
 * Estrutura:
 *  - Nav sticky com símbolo + sign-in
 *  - Hero (headline + subtitle + CTA + vault visual + 3 stats)
 *  - §01 How it works (3 cards)
 *  - §02 What's under the surface (dark, 4 feature cards)
 *  - §03 Philosophy (Polanyi quote + 4 philo items)
 *  - §04 Personas (3 cards)
 *  - CTA final em surface-alt
 *  - Footer
 *  - Modal auth (Google sign-in via useAuth)
 *  - Mobile menu full-screen
 */
export function LandingPage() {
  const { session, loading, signInWithGoogle } = useAuth()
  const navigate = useNavigate()
  const [authOpen, setAuthOpen] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)

  // Logged-in users skip the landing.
  useEffect(() => {
    if (!loading && session) navigate('/chat', { replace: true })
  }, [loading, session, navigate])

  // SEO title — restaurado on unmount.
  useEffect(() => {
    document.title = 'seGuru — One mind across everything you do'
    return () => { document.title = 'seGuru' }
  }, [])

  // Esc fecha modal/menu — atalho universal.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { setAuthOpen(false); setMenuOpen(false) }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  if (loading || session) return null

  return (
    <div className="d2-landing">
      <style>{INLINE_CSS}</style>

      {/* ── nav ─────────────────────────────────────────────── */}
      <nav className="nav">
        <div className="nav-inner">
          <a href="#" className="brand" aria-label="seGuru">
            <SeguruSymbol size={26} />
            <span>seGuru</span>
          </a>
          <div className="nav-links">
            <a href="#how-it-works">How it works</a>
            <a href="#features">The system</a>
            <button className="nav-cta" onClick={() => setAuthOpen(true)}>Sign in</button>
          </div>
          <button className="hamb" aria-label="Open menu" onClick={() => setMenuOpen(true)}>☰</button>
        </div>
      </nav>

      {/* ── hero ────────────────────────────────────────────── */}
      <section className="wrap hero">
        <div>
          <div className="badge reveal">Introspective productivity</div>
          <h1 className="headline reveal d1">
            You're not forgetting.<br /><em>It's just scattered.</em>
          </h1>
          <p className="subtitle reveal d2">
            seGuru is one mind across every project, client, and topic you're working on.
            It remembers everything — so nothing leaks, and the patterns surface before
            they cost you a lead, a call, or a prep.
          </p>
          <div className="ctas reveal d3">
            <button className="btn-primary" onClick={() => setAuthOpen(true)}>Start for free</button>
            <a href="#how-it-works" className="btn-link">
              See how it works
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                <line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" />
              </svg>
            </a>
          </div>
        </div>

        <div className="vault-wrap reveal d4">
          <div className="vault" aria-hidden="true">
            <svg viewBox="0 0 400 400">
              <path className="frame" d="M 20 20 L 380 20 L 380 280 Q 380 380 280 380 L 20 380 Z" />
              <line className="edge" x1="120" y1="100" x2="220" y2="180" />
              <line className="edge" x1="220" y1="180" x2="320" y2="120" />
              <line className="edge" x1="220" y1="180" x2="160" y2="280" />
              <line className="edge" x1="220" y1="180" x2="290" y2="280" />
              <line className="edge" x1="160" y1="280" x2="80" y2="220" />
              <line className="edge" x1="290" y1="280" x2="320" y2="120" />
              <line className="edge" x1="80" y1="220" x2="120" y2="100" />
              <line className="edge" x1="220" y1="180" x2="350" y2="200" />
              <line className="edge" x1="120" y1="100" x2="60" y2="140" />
              <circle className="node node-ink" cx="220" cy="180" r="24" />
              <circle className="node node-accent" cx="120" cy="100" r="11" />
              <circle className="node" cx="320" cy="120" r="10" />
              <circle className="node" cx="80" cy="220" r="9" />
              <circle className="node node-accent" cx="160" cy="280" r="11" />
              <circle className="node" cx="290" cy="280" r="10" />
              <circle className="node" cx="350" cy="200" r="7" />
              <circle className="node" cx="60" cy="140" r="7" />
              <text className="node-label node-label-invert" x="220" y="184" textAnchor="middle">memory</text>
              <text className="node-label" x="120" y="86" textAnchor="middle">João call</text>
              <text className="node-label" x="320" y="106" textAnchor="middle">Q2 strategy</text>
              <text className="node-label" x="160" y="302" textAnchor="middle">decision</text>
              <text className="node-label" x="290" y="302" textAnchor="middle">follow-up</text>
              <text className="node-label" x="80" y="240" textAnchor="middle">email</text>
            </svg>
          </div>
          <div className="stats">
            <div className="stat"><div className="num">15<em>d</em></div><div className="label">to indispensable</div></div>
            <div className="stat"><div className="num">∞</div><div className="label">sources</div></div>
            <div className="stat"><div className="num">0</div><div className="label">maintenance</div></div>
          </div>
        </div>
      </section>

      {/* ── §01 how it works ────────────────────────────────── */}
      <section id="how-it-works" className="section">
        <div className="wrap">
          <div className="section-head">
            <div className="section-num">01</div>
            <h2 className="section-title">How seGuru <em>thinks with you</em></h2>
          </div>
          <div className="grid-3">
            <article className="how-card">
              <span className="how-num">01</span>
              <h3>Connect.</h3>
              <p>Plug in email, calendar, and wherever else your work happens. Takes a minute.</p>
            </article>
            <article className="how-card">
              <span className="how-num">02</span>
              <h3>Remember.</h3>
              <p>seGuru reads along silently. No notes to take, no tags to write, no second app to maintain.</p>
            </article>
            <article className="how-card">
              <span className="how-num">03</span>
              <h3>Surface.</h3>
              <p>The patterns, reminders, and context find you. Before you'd think to ask.</p>
            </article>
          </div>
        </div>
      </section>

      {/* ── §02 features (dark) ─────────────────────────────── */}
      <section id="features" className="section section-dark">
        <div className="wrap">
          <div className="section-head">
            <div className="section-num">02</div>
            <h2 className="section-title">What's <em>under the surface</em></h2>
          </div>
          <div className="grid-2">
            <FeatCard
              icon={<><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" /><polyline points="22,6 12,13 2,6" /></>}
              title="Silent ingestion"
              body="Connect Gmail, Calendar, and docs once. seGuru reads in the background — extracting what's a decision, a hypothesis, a fact. You don't tag. You don't file."
            />
            <FeatCard
              icon={<><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></>}
              title="Meaning, not keywords"
              body="Ask 'what did I think about focus?' and seGuru finds it — even if you never used that word. Semantic search across every memory you've ever made."
            />
            <FeatCard
              icon={<><rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></>}
              title="Briefings before they matter"
              body="Before a call, a deadline, or a decision, seGuru surfaces what's relevant from across everything you've said, read, and decided."
            />
            <FeatCard
              icon={<path d="M22 12h-4l-3 9L9 3l-3 9H2" />}
              title="Patterns across threads"
              body="What happens in one project often echoes something you said in another. seGuru sees what you're too close to see."
            />
          </div>
        </div>
      </section>

      {/* ── §03 philosophy ──────────────────────────────────── */}
      <section className="section">
        <div className="wrap">
          <div className="philo">
            <div>
              <p className="quote">"We know more than we can <em>tell."</em></p>
              <p className="quote-author">— Michael Polanyi, philosopher of knowledge</p>
              <p className="quote-explain">
                seGuru is built on the idea that tacit knowledge — what you know but
                can't articulate — is the most valuable. The system helps you make it explicit.
              </p>
            </div>
            <div className="philo-list">
              <PhiloItem title="Not a repository" body="Repositories store. seGuru processes, correlates, and returns what matters — at the right moment, without being asked." />
              <PhiloItem title="It grows with you" body="The more you use it, the sharper it gets. By day 15, seGuru knows you well enough to surface connections you hadn't seen." />
              <PhiloItem title="It never gathers dust" body="Unused memories fade gradually. What's relevant stays within reach; what isn't recedes — like human memory, without the irreversible forgetting." />
              <PhiloItem title="Your data, always" body="Export everything. Delete anything. See exactly what the system knows about you. Full transparency." />
            </div>
          </div>
        </div>
      </section>

      {/* ── §04 personas ────────────────────────────────────── */}
      <section className="section">
        <div className="wrap">
          <div className="section-head">
            <div className="section-num">03</div>
            <h2 className="section-title">Built for people who <em>juggle.</em></h2>
          </div>
          <div className="grid-3">
            <article className="persona">
              <h3>Founders running multiple projects</h3>
              <p>You switch between four things an hour. seGuru holds the context so you don't have to.</p>
            </article>
            <article className="persona">
              <h3>Consultants with many clients</h3>
              <p>Every client has history. Every meeting builds on the last. seGuru remembers for you.</p>
            </article>
            <article className="persona">
              <h3>Executives across topics</h3>
              <p>Strategy, hiring, partners, product. seGuru connects what happens in one area to what you said in another.</p>
            </article>
          </div>
        </div>
      </section>

      {/* ── CTA final ───────────────────────────────────────── */}
      <section className="section section-alt">
        <div className="wrap cta-final">
          <div className="cta-num">04</div>
          <h2>Ready to stop<br /><em>losing what you know?</em></h2>
          <p>Free to try. No credit card, no commitment. seGuru becomes indispensable in 15 days — or you walk.</p>
          <button className="btn-primary" onClick={() => setAuthOpen(true)}>Start for free</button>
        </div>
      </section>

      {/* ── footer ──────────────────────────────────────────── */}
      <footer>
        <div className="wrap foot-inner">
          <a href="#" className="brand" aria-label="seGuru">
            <SeguruSymbol size={22} />
            <span>seGuru</span>
          </a>
          <p className="foot-copy">© 2026 seGuru. All rights reserved.</p>
        </div>
      </footer>

      {/* ── modal auth ──────────────────────────────────────── */}
      {authOpen && (
        <div className="modal-back" onClick={(e) => { if (e.target === e.currentTarget) setAuthOpen(false) }} role="dialog" aria-modal="true">
          <div className="modal">
            <button className="modal-close" onClick={() => setAuthOpen(false)} aria-label="Close">×</button>
            <div className="brand modal-brand">
              <SeguruSymbol size={28} />
              <span>seGuru</span>
            </div>
            <p className="modal-sub">Your <em>augmented</em> memory</p>
            <button className="google-btn" onClick={signInWithGoogle}>
              <svg width="18" height="18" viewBox="0 0 24 24">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#fff" opacity=".85" />
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A10.998 10.998 0 0 0 12 23z" fill="#fff" opacity=".7" />
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18A10.998 10.998 0 0 0 1 12c0 1.78.43 3.46 1.18 4.93l2.85-2.22.81-.62z" fill="#fff" opacity=".55" />
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#fff" opacity=".4" />
              </svg>
              Continue with Google
            </button>
            <p className="modal-disclaimer">
              By signing in you agree to our{' '}
              <a href="/termos">terms</a> and <a href="/privacidade">privacy</a>.
            </p>
          </div>
        </div>
      )}

      {/* ── mobile menu ─────────────────────────────────────── */}
      {menuOpen && (
        <div className="mob-menu">
          <div className="mob-menu-head">
            <a href="#" className="brand" onClick={() => setMenuOpen(false)}>
              <SeguruSymbol size={26} />
              <span>seGuru</span>
            </a>
            <button className="hamb" onClick={() => setMenuOpen(false)} aria-label="Close menu">×</button>
          </div>
          <div className="mob-menu-links">
            <a href="#how-it-works" onClick={() => setMenuOpen(false)}>How it <em>works</em></a>
            <a href="#features" onClick={() => setMenuOpen(false)}>The <em>system</em></a>
            <a onClick={() => { setMenuOpen(false); setAuthOpen(true) }}>Sign in</a>
          </div>
        </div>
      )}
    </div>
  )
}

// ── helpers de render ───────────────────────────────────────
function FeatCard({ icon, title, body }: { icon: React.ReactNode; title: string; body: string }) {
  return (
    <article className="feat-card">
      <div className="feat-icon">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          {icon}
        </svg>
      </div>
      <h3>{title}</h3>
      <p>{body}</p>
    </article>
  )
}

function PhiloItem({ title, body }: { title: string; body: string }) {
  return (
    <article className="philo-item">
      <h4>{title}</h4>
      <p>{body}</p>
    </article>
  )
}

// ── inline CSS específico da landing D2 ─────────────────────
// Usa tokens globais (--bg, --ink-1, --accent, --font-display, etc.)
// definidos em styles/d2/tokens.css. Mantido inline para que a
// landing seja auto-contida; se for re-utilizado, mover para
// styles/d2/landing.css.
const INLINE_CSS = `
.d2-landing { background: var(--bg); color: var(--ink-1); font-family: var(--font-ui); font-size: 15px; line-height: 1.55; -webkit-font-smoothing: antialiased; }
.d2-landing a { color: inherit; text-decoration: none; }

.d2-landing .wrap { max-width: 1200px; margin: 0 auto; padding: 0 24px; }
@media (min-width: 768px) { .d2-landing .wrap { padding: 0 48px; } }

.d2-landing .reveal { opacity: 0; transform: translateY(24px); animation: d2-reveal .7s var(--ease-out) forwards; }
.d2-landing .reveal.d1 { animation-delay: .08s; }
.d2-landing .reveal.d2 { animation-delay: .16s; }
.d2-landing .reveal.d3 { animation-delay: .24s; }
.d2-landing .reveal.d4 { animation-delay: .32s; }
@keyframes d2-reveal { to { opacity: 1; transform: translateY(0); } }

.d2-landing .nav { position: sticky; top: 0; z-index: 100; background: rgba(255,255,255,.85); backdrop-filter: blur(12px); -webkit-backdrop-filter: blur(12px); border-bottom: 1px solid var(--line); }
.d2-landing .nav-inner { display: flex; align-items: center; justify-content: space-between; padding: 18px 24px; }
@media (min-width: 768px) { .d2-landing .nav-inner { padding: 18px 48px; } }
.d2-landing .brand { display: flex; align-items: center; gap: 10px; font-family: var(--font-display); font-weight: 400; font-size: 22px; letter-spacing: -0.02em; }
.d2-landing .nav-links { display: none; gap: 32px; align-items: center; }
@media (min-width: 768px) { .d2-landing .nav-links { display: flex; } }
.d2-landing .nav-links a { font-size: 13px; color: var(--ink-2); letter-spacing: .02em; transition: color .15s; cursor: pointer; }
.d2-landing .nav-links a:hover { color: var(--ink-1); }
.d2-landing .nav-cta { font-size: 13px; background: var(--ink-1); color: #fff; padding: 8px 16px; border: 1px solid var(--ink-1); transition: background .15s, border-color .15s; cursor: pointer; font-family: var(--font-ui); }
.d2-landing .nav-cta:hover { background: var(--accent); border-color: var(--accent); }
.d2-landing .hamb { display: flex; background: none; border: 0; font-size: 22px; cursor: pointer; color: var(--ink-1); }
@media (min-width: 768px) { .d2-landing .hamb { display: none; } }

.d2-landing .hero { min-height: calc(100vh - 70px); display: grid; grid-template-columns: 1fr; gap: 48px; align-items: center; padding: 64px 0 96px; }
@media (min-width: 1024px) { .d2-landing .hero { grid-template-columns: 1.05fr 1fr; gap: 64px; padding: 96px 0; } }

.d2-landing .badge { display: inline-flex; align-items: center; gap: 8px; font-family: var(--font-mono); font-size: 11px; font-weight: 500; color: var(--accent); letter-spacing: .18em; text-transform: uppercase; margin-bottom: 32px; }
.d2-landing .badge::before { content: ""; width: 24px; height: 1px; background: var(--accent); }

.d2-landing .headline { font-family: var(--font-display); font-weight: 300; font-size: clamp(48px, 7vw, 92px); line-height: 1.0; letter-spacing: -0.025em; color: var(--ink-1); margin-bottom: 28px; }
.d2-landing .headline em { font-style: italic; color: var(--accent); font-weight: 400; }
.d2-landing .subtitle { font-size: 17px; color: var(--ink-2); font-weight: 400; line-height: 1.6; max-width: 480px; margin-bottom: 40px; }

.d2-landing .ctas { display: flex; flex-wrap: wrap; gap: 16px; align-items: center; }
.d2-landing .btn-primary { display: inline-flex; align-items: center; gap: 10px; background: var(--ink-1); color: #fff; font-size: 14px; font-weight: 500; padding: 14px 24px; border: 1px solid var(--ink-1); transition: background .15s, border-color .15s; cursor: pointer; font-family: var(--font-ui); }
.d2-landing .btn-primary:hover { background: var(--accent); border-color: var(--accent); }
.d2-landing .btn-link { font-size: 14px; color: var(--ink-1); display: inline-flex; align-items: center; gap: 8px; padding: 14px 4px; position: relative; cursor: pointer; }
.d2-landing .btn-link::after { content: ""; position: absolute; left: 4px; right: 4px; bottom: 10px; height: 1px; background: var(--ink-1); transform: scaleX(0); transform-origin: left; transition: transform .25s var(--ease-out); }
.d2-landing .btn-link:hover { color: var(--accent); }
.d2-landing .btn-link:hover::after { transform: scaleX(1); background: var(--accent); }
.d2-landing .btn-link svg { transition: transform .25s; }
.d2-landing .btn-link:hover svg { transform: translateX(4px); }

.d2-landing .vault-wrap { position: relative; display: flex; flex-direction: column; gap: 32px; }
.d2-landing .vault { aspect-ratio: 1; width: 100%; max-width: 560px; margin: 0 auto; position: relative; }
.d2-landing .vault svg { width: 100%; height: 100%; display: block; }
.d2-landing .vault .frame { fill: none; stroke: var(--line); stroke-width: 1; }
.d2-landing .vault .edge { stroke: var(--line-strong); stroke-width: 1; fill: none; opacity: .6; }
.d2-landing .vault .node { fill: var(--bg); stroke: var(--ink-1); stroke-width: 1.5; animation: d2-nodefloat 4s var(--ease-out) infinite; transform-origin: center; }
.d2-landing .vault .node-accent { fill: var(--accent); stroke: var(--accent); }
.d2-landing .vault .node-ink { fill: var(--ink-1); stroke: var(--ink-1); }
.d2-landing .vault .node-label { font-family: var(--font-mono); font-size: 8.5px; fill: var(--ink-3); letter-spacing: .05em; text-transform: uppercase; }
.d2-landing .vault .node-label-invert { fill: #FAFAF7; }
@keyframes d2-nodefloat { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-6px); } }
.d2-landing .vault .node:nth-of-type(2) { animation-delay: .4s; }
.d2-landing .vault .node:nth-of-type(3) { animation-delay: .8s; }
.d2-landing .vault .node:nth-of-type(4) { animation-delay: 1.2s; }
.d2-landing .vault .node:nth-of-type(5) { animation-delay: 1.6s; }

.d2-landing .stats { display: grid; grid-template-columns: repeat(3, 1fr); border-top: 1px solid var(--line); padding-top: 24px; gap: 16px; }
.d2-landing .stat .num { font-family: var(--font-display); font-weight: 300; font-size: 36px; color: var(--ink-1); line-height: 1; letter-spacing: -0.02em; }
.d2-landing .stat .num em { color: var(--accent); font-style: normal; font-weight: 400; }
.d2-landing .stat .label { font-family: var(--font-mono); font-size: 10px; color: var(--ink-3); text-transform: uppercase; letter-spacing: .14em; margin-top: 8px; line-height: 1.4; }

.d2-landing .section { padding: 96px 0; border-top: 1px solid var(--line); }
.d2-landing .section-dark { background: var(--ink-1); color: #FAFAF7; border-top-color: transparent; }
.d2-landing .section-alt { background: var(--surface-alt); }

.d2-landing .section-head { display: grid; grid-template-columns: 1fr; gap: 24px; margin-bottom: 64px; }
@media (min-width: 1024px) { .d2-landing .section-head { grid-template-columns: auto 1fr; gap: 80px; align-items: end; } }
.d2-landing .section-num { font-family: var(--font-display); font-weight: 300; font-size: clamp(56px, 9vw, 120px); line-height: .85; color: var(--accent); letter-spacing: -0.04em; }
.d2-landing .section-title { font-family: var(--font-display); font-weight: 300; font-size: clamp(32px, 4.5vw, 56px); line-height: 1.05; letter-spacing: -0.02em; color: inherit; max-width: 780px; }
.d2-landing .section-title em { font-style: italic; color: var(--accent); font-weight: 400; }
.d2-landing .section-dark .section-title { color: #FAFAF7; }

.d2-landing .grid-3 { display: grid; grid-template-columns: 1fr; gap: 1px; background: var(--line); border: 1px solid var(--line); }
@media (min-width: 768px) { .d2-landing .grid-3 { grid-template-columns: repeat(3, 1fr); } }
.d2-landing .how-card { background: var(--bg); padding: 40px 32px; display: flex; flex-direction: column; gap: 16px; transition: background .2s; }
.d2-landing .how-card:hover { background: var(--surface-alt); }
.d2-landing .how-num { font-family: var(--font-mono); font-size: 11px; color: var(--ink-3); letter-spacing: .18em; }
.d2-landing .how-card h3 { font-family: var(--font-display); font-weight: 400; font-size: 26px; line-height: 1.15; color: var(--ink-1); letter-spacing: -0.01em; }
.d2-landing .how-card p { font-size: 14px; color: var(--ink-2); line-height: 1.65; }

.d2-landing .grid-2 { display: grid; grid-template-columns: 1fr; gap: 1px; background: rgba(250,250,247,.08); border: 1px solid rgba(250,250,247,.08); }
@media (min-width: 768px) { .d2-landing .grid-2 { grid-template-columns: repeat(2, 1fr); } }
.d2-landing .feat-card { background: var(--ink-1); padding: 40px 32px; display: flex; flex-direction: column; gap: 16px; transition: background .2s; color: #FAFAF7; }
.d2-landing .feat-card:hover { background: #1A1A1F; }
.d2-landing .feat-icon { width: 36px; height: 36px; display: flex; align-items: center; justify-content: center; color: var(--accent); border: 1px solid rgba(250,250,247,.12); }
.d2-landing .feat-icon svg { width: 20px; height: 20px; }
.d2-landing .feat-card h3 { font-family: var(--font-display); font-weight: 400; font-size: 22px; line-height: 1.2; color: #FAFAF7; letter-spacing: -0.01em; }
.d2-landing .feat-card p { font-size: 14px; color: #C8C1AE; line-height: 1.65; }

.d2-landing .philo { display: grid; grid-template-columns: 1fr; gap: 48px; }
@media (min-width: 1024px) { .d2-landing .philo { grid-template-columns: 1.1fr 1fr; gap: 96px; } }
.d2-landing .quote { font-family: var(--font-display); font-style: italic; font-weight: 300; font-size: clamp(28px, 3.5vw, 44px); line-height: 1.2; letter-spacing: -0.015em; color: var(--ink-1); margin-bottom: 32px; position: relative; padding-left: 24px; }
.d2-landing .quote em { display: block; margin-top: 8px; color: var(--accent); font-weight: 400; }
.d2-landing .quote::before { content: ""; position: absolute; left: 0; top: 8px; bottom: 8px; width: 2px; background: var(--accent); }
.d2-landing .quote-author { font-family: var(--font-mono); font-size: 11px; letter-spacing: .16em; text-transform: uppercase; color: var(--ink-3); margin-bottom: 24px; }
.d2-landing .quote-explain { font-size: 15px; color: var(--ink-2); line-height: 1.65; max-width: 480px; }
.d2-landing .philo-list { display: flex; flex-direction: column; gap: 0; border-top: 1px solid var(--line); }
.d2-landing .philo-item { padding: 28px 0 28px 24px; border-bottom: 1px solid var(--line); position: relative; transition: padding-left .25s; }
.d2-landing .philo-item::before { content: ""; position: absolute; left: 0; top: 28px; bottom: 28px; width: 2px; background: transparent; transition: background .25s; }
.d2-landing .philo-item:hover { padding-left: 32px; }
.d2-landing .philo-item:hover::before { background: var(--accent); }
.d2-landing .philo-item h4 { font-family: var(--font-display); font-weight: 400; font-size: 20px; color: var(--ink-1); margin-bottom: 6px; letter-spacing: -0.01em; }
.d2-landing .philo-item p { font-size: 14px; color: var(--ink-3); line-height: 1.6; }

.d2-landing .persona { background: var(--bg); padding: 40px 32px; display: flex; flex-direction: column; gap: 14px; transition: background .2s; }
.d2-landing .persona:hover { background: var(--surface-alt); }
.d2-landing .persona h3 { font-family: var(--font-display); font-weight: 400; font-size: 22px; line-height: 1.2; color: var(--ink-1); letter-spacing: -0.01em; }
.d2-landing .persona p { font-size: 14px; color: var(--ink-2); line-height: 1.65; }

.d2-landing .cta-final { text-align: center; padding: 96px 0; }
.d2-landing .cta-num { font-family: var(--font-display); font-weight: 300; font-size: clamp(56px, 8vw, 96px); line-height: .85; color: var(--accent); letter-spacing: -0.04em; margin-bottom: 32px; }
.d2-landing .cta-final h2 { font-family: var(--font-display); font-weight: 300; font-size: clamp(40px, 6vw, 72px); line-height: 1.05; letter-spacing: -0.025em; color: var(--ink-1); margin-bottom: 24px; }
.d2-landing .cta-final h2 em { font-style: italic; color: var(--accent); font-weight: 400; }
.d2-landing .cta-final p { font-size: 17px; color: var(--ink-2); max-width: 520px; margin: 0 auto 40px; line-height: 1.55; }

.d2-landing footer { padding: 48px 0; border-top: 1px solid var(--line); background: var(--bg); }
.d2-landing .foot-inner { display: flex; flex-direction: column; gap: 16px; justify-content: space-between; align-items: flex-start; }
@media (min-width: 768px) { .d2-landing .foot-inner { flex-direction: row; align-items: center; } }
.d2-landing .foot-copy { font-family: var(--font-mono); font-size: 11px; color: var(--ink-3); letter-spacing: .08em; }

.d2-landing .modal-back { position: fixed; inset: 0; z-index: 200; background: rgba(11,11,14,.45); backdrop-filter: blur(8px); -webkit-backdrop-filter: blur(8px); display: flex; align-items: center; justify-content: center; padding: 24px; }
.d2-landing .modal { background: var(--bg); border: 1px solid var(--line); border-radius: var(--motif-radius); max-width: 440px; width: 100%; padding: 48px 40px; position: relative; animation: d2-modalin .25s var(--ease-out); }
@keyframes d2-modalin { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); } }
.d2-landing .modal-close { position: absolute; top: 20px; right: 20px; background: none; border: 0; font-size: 22px; cursor: pointer; color: var(--ink-3); line-height: 1; padding: 4px; }
.d2-landing .modal-close:hover { color: var(--ink-1); }
.d2-landing .modal-brand { margin-bottom: 8px; justify-content: center; }
.d2-landing .modal-sub { font-family: var(--font-display); font-size: 18px; font-weight: 300; font-style: italic; color: var(--ink-2); text-align: center; margin-bottom: 32px; }
.d2-landing .modal-sub em { color: var(--accent); font-style: normal; font-weight: 400; }
.d2-landing .google-btn { width: 100%; display: flex; align-items: center; justify-content: center; gap: 12px; padding: 14px; background: var(--ink-1); color: #fff; border: 1px solid var(--ink-1); font-size: 14px; font-weight: 500; cursor: pointer; transition: background .15s, border-color .15s; font-family: var(--font-ui); }
.d2-landing .google-btn:hover { background: var(--accent); border-color: var(--accent); }
.d2-landing .modal-disclaimer { margin-top: 16px; font-size: 12px; color: var(--ink-3); text-align: center; line-height: 1.5; }
.d2-landing .modal-disclaimer a { color: var(--ink-2); text-decoration: underline; text-decoration-color: var(--accent); text-underline-offset: 3px; }

.d2-landing .mob-menu { position: fixed; inset: 0; z-index: 150; background: var(--bg); padding: 24px; display: flex; flex-direction: column; }
.d2-landing .mob-menu-head { display: flex; justify-content: space-between; align-items: center; margin-bottom: 48px; }
.d2-landing .mob-menu-links { display: flex; flex-direction: column; gap: 24px; }
.d2-landing .mob-menu-links a { font-family: var(--font-display); font-weight: 400; font-size: 32px; color: var(--ink-1); letter-spacing: -0.01em; cursor: pointer; }
.d2-landing .mob-menu-links a em { font-style: italic; color: var(--accent); }
`
